import * as React from "react";
import { AssigneeDropdown } from "@adsk/acc-issues-common/components/input/ProjectMemberDropdown/AssigneeDropdown";
import { useGetLevelOneUserShouldIncludeMemberFn } from "@adsk/acc-issues-common/hooks/data/useGetLevelOneUserShouldIncludeMemberFn";
import { useUserDisplayName } from "@adsk/acc-issues-common/hooks/data/useUserDisplayName";
import { CompactEditableFieldWrapper } from "@adsk/acc-issues-common/components/primitive/EditableFieldWrapper";
import { useFieldTestId } from "../../../utils/components/IssueFieldPresentationContext";
import { DefaultEditFieldInterface, IssueChange } from "./typesForFields";
import { isAttributeEditable } from "../../../utils/data/entityPermissions";
import { PermittedAction } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { fetchIssueViewersByIdAction } from "@adsk/acc-issues-core/state/redux/ducks/issuesViewers";
import { useDispatch } from "@adsk/acc-issues-core/state/redux/hooks";

// This Field is similar to the one in IssuesTab/TemplatesConfig/IssueTemplateDetailPanel/detail-panel-fields/...
// In case any necessary change is needed, it would be nice check on the other file as well
export const AssignedToField = ({
  issue,
  onChange,
  containerRef,
  required = false,
  viewOnly = false,
  shouldIncludeEmpty = false,
}: DefaultEditFieldInterface & { shouldIncludeEmpty?: boolean }) => {
  const dispatch = useDispatch();
  const { T } = useTranslations();

  const levelOneUserShouldIncludeMemberFn =
    useGetLevelOneUserShouldIncludeMemberFn();
  const shouldIncludeMemberFn = !issue.permittedActions.includes(
    PermittedAction.AssignAll
  )
    ? levelOneUserShouldIncludeMemberFn
    : undefined;

  const isDisabled = viewOnly || !isAttributeEditable(issue, "assignedTo");

  const fieldValue = useUserDisplayName(issue.assignedTo);

  const renderInput = React.useCallback(
    ({ onChange: inlineEditOnChange }: any) => (
      <AssigneeDropdown
        selectedAssigneeId={issue.assignedTo}
        selectedWatchers={issue.watcherObjects}
        onChange={result => {
          const change: IssueChange = {
            assignedTo: result.assignedTo,
            assignedToType: result.assignedToType,
            ...(result.watchers && {
              watchers: result.watchers.map(watcher => watcher.id),
            }),
          };
          inlineEditOnChange(change);
          onChange(change, {
            onSuccess: () => {
              dispatch(
                fetchIssueViewersByIdAction({
                  issueId: issue.id,
                  forceFetch: true,
                })
              );
            },
          });
        }}
        onClose={() => inlineEditOnChange(undefined)}
        required={required}
        shouldIncludeEmpty={shouldIncludeEmpty}
        shouldIncludeMemberFn={shouldIncludeMemberFn}
        clearable={issue.permittedActions.includes(
          PermittedAction.ClearAssignee
        )}
        defaultIsOpen={true}
      />
    ),
    [
      issue,
      dispatch,
      onChange,
      required,
      shouldIncludeEmpty,
      shouldIncludeMemberFn,
    ]
  );

  const testId = useFieldTestId("assigned-to-field");

  return (
    <div data-testid={testId}>
      <CompactEditableFieldWrapper
        fieldKey="assignedTo"
        label={T.translate("issueAttributes.assignedTo")}
        value={fieldValue}
        disabled={isDisabled}
        containerRef={containerRef}
        renderInput={renderInput}
        required={required}
        showDisabledReason={!viewOnly}
      />
    </div>
  );
};
